/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  OrganisationAdminRemoveArgs,
  OrganisationAdminUpdateArgs,
  OrganisationUserBanByUserIdArgs,
  OrganisationUserInviteByUserIdArgs,
  OrganisationUserManagementChannelStatus,
  OrganisationUserManagementResult,
  OrganisationUserManagementStatus,
  OrganisationUserRemoveFromChannelArgs,
  OrganisationUserUnbanByUserIdArgs,
  OrganisationUserUpdateChannelRoleArgs,
  OrganisationUsersArgs,
  User,
  organisationApi,
} from '../services';
import { PaginationInfo } from '../shared';

interface OrganisationUsersState {
  isFetched: boolean;
  users: User[];
  pageInfo: PaginationInfo;
}

const initialState: OrganisationUsersState = {
  isFetched: false,
  users: [],
  pageInfo: {
    page: 0,
    perPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    totalItems: 0,
    totalPages: 0,
  },
};

const getOrganisationUsersHandler = async (args: OrganisationUsersArgs, { dispatch }: any) => {
  return dispatch(organisationApi.endpoints.organisationUsers.initiate({ ...args })).unwrap();
};

export const getOrganisationUsers = createAsyncThunk(
  'getOrganisationUsers',
  getOrganisationUsersHandler
);

export const loadMoreOrganisationUsers = createAsyncThunk(
  'getOrganisationUsers/loadMore',
  getOrganisationUsersHandler
);

export const organisationAdminUpdate = createAsyncThunk(
  'organisationAdminUpdate',
  async (args: OrganisationAdminUpdateArgs, { dispatch }: any) => {
    const updated = await dispatch(
      organisationApi.endpoints.organisationAdminUpdate.initiate({ ...args })
    ).unwrap();
    return { ...args, updated };
  }
);

export const organisationAdminRemove = createAsyncThunk(
  'organisationAdminRemove',
  async (args: OrganisationAdminRemoveArgs, { dispatch }: any) => {
    const userManagementResult: OrganisationUserManagementResult[] = await dispatch(
      organisationApi.endpoints.organisationAdminRemove.initiate({ ...args })
    ).unwrap();
    return { ...args, userManagementResult };
  }
);

export const organisationUserRemoveFromChannel = createAsyncThunk(
  'organisationUserRemoveFromChannel',
  async (args: OrganisationUserRemoveFromChannelArgs, { dispatch }: any) => {
    const userManagementResult: OrganisationUserManagementResult[] = await dispatch(
      organisationApi.endpoints.organisationUserRemoveFromChannel.initiate({ ...args })
    ).unwrap();
    return { ...args, userManagementResult };
  }
);

export const organisationUserInviteByUserId = createAsyncThunk(
  'organisationUserInviteByUserId',
  async (args: OrganisationUserInviteByUserIdArgs, { dispatch }: any) => {
    const userManagementResult: OrganisationUserManagementResult[] = await dispatch(
      organisationApi.endpoints.organisationUserInviteByUserId.initiate({ ...args })
    ).unwrap();
    return { ...args, userManagementResult };
  }
);

export const organisationUserUpdateChannelRole = createAsyncThunk(
  'organisationUserUpdateChannelRole',
  async (args: OrganisationUserUpdateChannelRoleArgs, { dispatch }: any) => {
    const userManagementResult: OrganisationUserManagementResult[] = await dispatch(
      organisationApi.endpoints.organisationUserUpdateChannelRole.initiate({ ...args })
    ).unwrap();
    return { ...args, userManagementResult };
  }
);

export const organisationUserBanByUserId = createAsyncThunk(
  'organisationUserBanByUserId',
  async (args: OrganisationUserBanByUserIdArgs, { dispatch }: any) => {
    const userManagementResult: OrganisationUserManagementResult[] = await dispatch(
      organisationApi.endpoints.organisationUserBanByUserId.initiate({ ...args })
    ).unwrap();
    return { ...args, userManagementResult };
  }
);

export const organisationUserUnbanByUserId = createAsyncThunk(
  'organisationUserUnbanByUserId',
  async (args: OrganisationUserUnbanByUserIdArgs, { dispatch }: any) => {
    const userManagementResult: OrganisationUserManagementResult[] = await dispatch(
      organisationApi.endpoints.organisationUserUnbanByUserId.initiate({ ...args })
    ).unwrap();
    return { ...args, userManagementResult };
  }
);

const OrganisationUsersSlice = createSlice({
  name: 'organisationUsers',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOrganisationUsers.pending, (state) => {
        state.isFetched = false;
      })
      .addCase(getOrganisationUsers.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;

        state.isFetched = true;
        state.users = items;
        state.pageInfo = pageInfo;
      })
      .addCase(loadMoreOrganisationUsers.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;

        state.users.push(...items);
        state.pageInfo = pageInfo;
      })
      .addCase(organisationUserRemoveFromChannel.fulfilled, (state, action) => {
        const { userManagementResult, userIds } = action.payload;

        if (!userManagementResult.length) {
          return;
        }

        state.users
          .filter(({ id }) => userIds.includes(id))
          .forEach((user) => {
            const resultUser = userManagementResult.find((result) => result.user.id === user.id);

            if (!resultUser) {
              return;
            }

            const resultChannelIds = resultUser.channels
              .filter(({ status }) => status === OrganisationUserManagementChannelStatus.OK)
              .map(({ channel: { id } }) => id);

            user.accessedChannels = user.accessedChannels.filter(
              ({ id }) => !resultChannelIds.includes(id)
            );
          });
      })
      .addCase(organisationUserInviteByUserId.fulfilled, (state, action) => {
        const { userManagementResult, userIds, role } = action.payload;

        if (!userManagementResult.length) {
          return;
        }

        state.users
          .filter(({ id }) => userIds.includes(id))
          .forEach((user) => {
            const resultUser = userManagementResult.find((result) => result.user.id === user.id);

            if (!resultUser) {
              return;
            }

            const resultChannelIds = resultUser.channels
              .filter(({ status }) => status === OrganisationUserManagementChannelStatus.OK)
              .map(({ channel: { id } }) => id);

            resultChannelIds.forEach((id) =>
              user.accessedChannels.push({ id, name: '', subdomain: '', role })
            );
          });
      })
      .addCase(organisationUserUpdateChannelRole.fulfilled, (state, action) => {
        const { userManagementResult, userIds, role } = action.payload;

        if (!userManagementResult.length) {
          return;
        }

        state.users
          .filter(({ id }) => userIds.includes(id))
          .forEach((user) => {
            const resultUser = userManagementResult.find((result) => result.user.id === user.id);

            if (!resultUser) {
              return;
            }

            const resultChannelIds = resultUser.channels
              .filter(({ status }) => status === OrganisationUserManagementChannelStatus.OK)
              .map(({ channel: { id } }) => id);

            user.accessedChannels
              .filter(({ id }) => resultChannelIds.includes(id))
              .forEach((channel) => (channel.role = role));
          });
      })
      .addCase(organisationUserBanByUserId.fulfilled, (state, action) => {
        const { userManagementResult, userIds } = action.payload;

        if (!userManagementResult.length) {
          return;
        }

        state.users
          .filter(({ id }) => userIds.includes(id))
          .forEach((user) => {
            const resultUser = userManagementResult.find((result) => result.user.id === user.id);

            if (!resultUser) {
              return;
            }

            if (resultUser.status === OrganisationUserManagementStatus.OK) {
              user.isBanned = true;
            }
          });
      })
      .addCase(organisationUserUnbanByUserId.fulfilled, (state, action) => {
        const { userManagementResult, userIds } = action.payload;

        if (!userManagementResult.length) {
          return;
        }

        state.users
          .filter(({ id }) => userIds.includes(id))
          .forEach((user) => {
            const resultUser = userManagementResult.find((result) => result.user.id === user.id);

            if (!resultUser) {
              return;
            }

            if (resultUser.status === OrganisationUserManagementStatus.OK) {
              user.isBanned = false;
            }
          });
      });
  },
});

export default OrganisationUsersSlice.reducer;
